import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import "bpmn-js/dist/assets/diagram-js.css";
import type { IWorkFlowVersionDto } from "@kuika/kuika-cl-model/src/dtos/workflow.dto";
import type { AxiosResponse } from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { WorkFlowService } from "../../../../services/workflow-service";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import { WorkflowBpmnDiff } from "../../branching/workflow-bpmn-diff";
import {
  IVersionComparePair,
  IVersionRow,
  toComparePair,
  versionLabel,
  VERSION_COMPARE_COUNT
} from "./version-history";
import { versionChangedNodeIds } from "./version-xml-diff";
import "./version-compare-modal.scss";

/**
 * Two diagrams side by side need roughly twice the width of one, capped against the viewport so
 * the dialog cannot outgrow a laptop screen (rc-dialog would otherwise scroll horizontally).
 */
const MODAL_WIDTH: string = "min(1240px, 92vw)";

/** `failed` is the request itself not completing; two empty diagrams are reported separately. */
type VersionCompareStatus = "loading" | "ready" | "failed";

interface IVersionComparePayload {
  /**
   * The pair this payload answers. Rendering is gated on it matching the open pair, so changing
   * the selection cannot flash the previous comparison.
   */
  requestKey: string;
  status: VersionCompareStatus;
  beforeXml: string;
  afterXml: string;
}

const INITIAL_PAYLOAD: IVersionComparePayload = { requestKey: "", status: "loading", beforeXml: "", afterXml: "" };

/**
 * The service reports a failed request by RESOLVING with the error object, so neither the response
 * nor its data is guaranteed despite the Axios typing. A version with no XML reads as "no diagram",
 * which is exactly what the diff pane renders for a missing side.
 */
const readXml = (response: AxiosResponse<IWorkFlowVersionDto> | undefined): string => {
  const dto: IWorkFlowVersionDto | undefined = response?.data;
  return dto?.xml?.trim() ?? "";
};

interface IVersionCompareUnavailableProps {
  status: VersionCompareStatus;
}

/** Shown only when there is genuinely no comparison to draw — never in place of one live pane. */
const VersionCompareUnavailable = ({ status }: IVersionCompareUnavailableProps): React.ReactElement => {
  const isFailed: boolean = status === "failed";
  const title: string = isFailed ? "These versions could not be loaded" : "Nothing to compare";
  const description: string = isFailed
    ? "The request for the two diagrams did not complete. Close the dialog and try again."
    : "Neither version has a saved BPMN diagram, so there is no difference to show.";
  return <KuikaEmptyState variant="page" svgName="automation" customName={title} description={description} />;
};

export interface IVersionCompareModalProps {
  /** The two versions to diff; `undefined` or any other count keeps the modal closed. */
  versions?: IVersionRow[];
  onClose: () => void;
}

/**
 * Side-by-side diff of two exported versions' BPMN diagrams.
 *
 * Opened from the Version History selection toolbar, which enables Compare Versions only with
 * exactly two rows selected. The panes come from `WorkflowBpmnDiff` — the same read-only diff the
 * branching tool uses — so the changed-node highlight behaves identically in both places.
 */
const VersionCompareModal = ({ versions, onClose }: IVersionCompareModalProps): React.ReactElement => {
  const pair: IVersionComparePair | undefined = useMemo(
    (): IVersionComparePair | undefined => toComparePair(versions),
    [versions]
  );
  const olderId: string | undefined = pair?.older.id;
  const newerId: string | undefined = pair?.newer.id;
  // Identity of the request, so the effect re-fetches on a changed selection but not on a parent
  // re-render that hands down a fresh array of the same two rows.
  const requestKey: string = olderId && newerId ? `${olderId}|${newerId}` : "";
  const [payload, setPayload] = useState<IVersionComparePayload>(INITIAL_PAYLOAD);

  useEffect((): (() => void) | undefined => {
    if (!olderId || !newerId) return undefined;

    let cancelled: boolean = false;
    // Both versions are independent reads — no reason to serialise them.
    Promise.all([WorkFlowService.GetVersion(olderId), WorkFlowService.GetVersion(newerId)])
      .then((responses: AxiosResponse<IWorkFlowVersionDto>[]): void => {
        if (cancelled) return;
        setPayload({
          requestKey,
          status: "ready",
          beforeXml: readXml(responses[0]),
          afterXml: readXml(responses[1])
        });
      })
      .catch((): void => {
        if (cancelled) return;
        setPayload({ requestKey, status: "failed", beforeXml: "", afterXml: "" });
      });

    return (): void => {
      cancelled = true;
    };
  }, [requestKey, olderId, newerId]);

  // A payload for a previous selection is not this selection's answer — treat it as still loading.
  const isCurrent: boolean = !!requestKey && payload.requestKey === requestKey;
  const status: VersionCompareStatus = isCurrent ? payload.status : "loading";
  const beforeXml: string = isCurrent ? payload.beforeXml : "";
  const afterXml: string = isCurrent ? payload.afterXml : "";

  const changedNodeIds: string[] = useMemo(
    (): string[] => versionChangedNodeIds(beforeXml, afterXml),
    [beforeXml, afterXml]
  );

  const hasAnyDiagram: boolean = !!beforeXml || !!afterXml;
  const header: string = pair ? `Compare V${pair.older.versionNumber} → V${pair.newer.versionNumber}` : "";

  return (
    <KModal
      className="kuika-version-compare"
      showModal={!!pair}
      closeModal={onClose}
      header={header}
      isLoading={status === "loading"}
      width={MODAL_WIDTH}
    >
      {/* The body keeps a floor height in every state, so the spinner has a box to centre in and
          the dialog does not resize when the two diagrams land. */}
      <div className="kuika-version-compare__body" data-testid="processVersionCompare">
        {status === "ready" && hasAnyDiagram && pair && (
          <WorkflowBpmnDiff
            beforeXml={beforeXml || undefined}
            afterXml={afterXml || undefined}
            changedNodeIds={changedNodeIds}
            beforeLabel={versionLabel(pair.older)}
            afterLabel={versionLabel(pair.newer)}
          />
        )}
        {(status === "failed" || (status === "ready" && !hasAnyDiagram)) && (
          <div className="kuika-version-compare__unavailable">
            <VersionCompareUnavailable status={status} />
          </div>
        )}
      </div>
    </KModal>
  );
};

export default VersionCompareModal;
